// Helpers for the six influence KPI cards (plan 2190). "Influenced" is reach, never revenue TEC created; the lift,
// speed and size are associations at equal age and deal size, always with n, open share and an interval.
import { fmtPts, fmtPct, fmtInt, fmtInterval } from './influenceScorecard';

export function buildKpiQuery({ startDate, endDate, tecProgramTagId, tier, window, eventId, tagId, participantSegment, geography }) {
  const p = new URLSearchParams();
  if (startDate && endDate) { p.set('startDate', startDate); p.set('endDate', endDate); }
  if (tecProgramTagId) p.set('tecProgramTagId', String(tecProgramTagId));
  if (tier) p.set('tier', tier);
  if (!(startDate && endDate) && window) p.set('window', window);
  if (eventId) p.set('event_id', String(eventId));
  if (tagId) p.set('tagId', String(tagId));
  if (participantSegment && participantSegment !== 'all') p.set('participantSegment', participantSegment);
  if (geography) p.set('geo', geography);
  return p.toString();
}

export const kpiCardsActive = (cutover, data) => !!(cutover && data?.available);

const REASONS = {
  filter_not_supported: 'this filter is not supported',
  program_not_mapped: 'this TEC Program has no products mapped yet (Tag Admin > Program Products)',
  no_shared_products: 'the event and the TEC Program share no product',
  influence_reloading: 'the numbers for this view are reloading, try again in a minute',
  period_not_precomputed: 'this period is not precomputed (custom range, or a new quarter before tonight\'s run)',
  event_filter_not_supported: 'the event filter is not supported',
  product_filter_not_supported: 'the product filter is not supported',
  segment_not_supported: 'participant segments are not supported',
  geography_filter_not_supported: 'the geography filter is not supported',
};
export const kpiReasonText = (reason) => REASONS[reason] || 'no influence scorecard rows for this view yet';

export const signedUsd = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  const s = a >= 1e6 ? `$${(a / 1e6).toFixed(2)}M` : a >= 1e3 ? `$${(a / 1e3).toFixed(1)}K` : `$${a.toFixed(0)}`;
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${s}`;
};
const fmtIntervalUsd = (lo, hi) => (lo == null || hi == null ? '—' : `[${signedUsd(lo)}, ${signedUsd(hi)}]`);

// The lines under each rate/difference card value: interval, n, open share, horizon.
export function liftLines(c) {
  if (c.insufficient) return { value: 'insufficient', lines: [c.reason, `n = ${fmtInt(c.n ?? 0)} ${c.id === 'size' ? 'won' : 'closed'} deals with full follow-up`], insufficient: true };
  return {
    value: fmtPts(c.lift_pts),
    lines: [
      `95% interval ${fmtInterval(c.ci_lo_pts, c.ci_hi_pts)} · within ${c.horizon_days} days`,
      `n = ${fmtInt(c.n)} closed deals · ${fmtPct(c.open_share_pct)} still open`,
    ],
  };
}

export function speedLines(c) {
  if (c.insufficient) return { value: 'insufficient', lines: [c.reason, `n = ${fmtInt(c.n ?? 0)} ${c.id === 'size' ? 'won' : 'closed'} deals with full follow-up`], insufficient: true };
  const med = c.median && !c.median.insufficient && c.median.treated != null
    ? `Median days to close (deals closed within ${c.horizon_days}): ${c.median.treated} vs ${c.median.baseline ?? '—'}`
    : null;
  return {
    value: fmtPts(c.diff_pts),
    lines: [
      `closed within ${c.horizon_days} days: ${fmtPct(c.treated_rate_pct, 1)} vs ${fmtPct(c.baseline_rate_pct, 1)} comparable`,
      `95% interval ${fmtInterval(c.ci_lo_pts, c.ci_hi_pts)} · n = ${fmtInt(c.n)} · ${fmtPct(c.open_share_pct)} still open`,
      med,
    ].filter(Boolean),
  };
}

export function sizeLines(c) {
  if (c.insufficient) return { value: 'insufficient', lines: [c.reason, `n = ${fmtInt(c.n ?? 0)} ${c.id === 'size' ? 'won' : 'closed'} deals with full follow-up`], insufficient: true };
  return {
    value: signedUsd(c.diff),
    lines: [
      `avg won deal: ${signedUsd(c.treated_avg).replace('+', '')} vs ${signedUsd(c.baseline_avg).replace('+', '')} comparable`,
      `95% interval ${fmtIntervalUsd(c.ci_lo, c.ci_hi)} · n = ${fmtInt(c.n)} won deals`,
    ],
  };
}
